import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { cssMap, keyframes } from '@compiled/react';

import { token, useThemeObserver } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

// The below colors do not have tokens at the time of impl, but come from https://www.figma.com/design/BIqP8imOLa7DUmDsmAdjh3/%E2%9C%A6-AI-Foundations?node-id=2092-523&t=Q0WLZ3vnqiLltUT6-0
// Light Theme Colors
export const LT_YELLOW = '#FCA700';
export const LT_GREEN = '#6A9A23';
export const LT_BLUE = '#1868DB';
export const LT_PURPLE = '#AF59E0';

// Dark Theme Colors
export const DT_YELLOW = '#FCA700';
export const DT_GREEN = '#82B536';
export const DT_BLUE = '#357DE8';
export const DT_PURPLE = '#BF63F3';

const svgStrokeAnimation = keyframes({
	from: {
		strokeDashoffset: 'var(--stroke-perimeter)',
	},
	to: {
		strokeDashoffset: 0,
	},
});

const styles = cssMap({
	svg: {
		position: 'absolute',
		fill: 'transparent',
		positionArea: 'bottom',
	},
	container: {
		position: 'relative',
		strokeOpacity: 0,
		'&:hover': {
			animation: `${svgStrokeAnimation} 1.5s linear infinite`,
			strokeOpacity: 1,
		},
	},
});

const rectProps = {
	line1: {
		dashMultiplier: 0,
		light: LT_YELLOW,
		dark: DT_YELLOW,
	},
	line2: {
		dashMultiplier: 0.66,
		light: LT_GREEN,
		dark: DT_GREEN,
	},
	line3: {
		dashMultiplier: 0.46,
		light: LT_BLUE,
		dark: DT_BLUE,
	},
	line4: {
		dashMultiplier: 0.2,
		light: LT_PURPLE,
		dark: DT_PURPLE,
	},
} as const;

/**
 * Produces an animated "gradient" border. Typically, would use conic-gradient to handle but that produces uneven colors for rectangles.
 * Instead, we use a svg for each color and animate the stroke. While this still doesn't produce a 100% accurate distribution of each color (25%)
 * it looks much better than the conic-gradient option.
 */
export const AnimatedGradientBorder = ({
	children,
	borderRadius = true,
}: {
	children: React.ReactNode;
	borderRadius?: boolean;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const { colorMode } = useThemeObserver();
	const theme = colorMode === 'dark' ? 'dark' : 'light';

	const [perimeter, setPerimeter] = useState(0);

	useEffect(() => {
		const rect = ref?.current?.getBoundingClientRect();
		const height = rect?.height || 0;
		const width = rect?.width || 0;

		setPerimeter(height * 2 + width * 2);
	}, []);

	const calculateDashArray = useCallback(
		(key: string) => {
			const multiplier = rectProps[key].dashMultiplier;
			const dashArrayLength = Math.round(multiplier * perimeter);
			return multiplier === 0 ? 0 : `${dashArrayLength} ${perimeter - dashArrayLength}`;
		},
		[perimeter],
	);

	if (!expValEquals('cc_fd_aifc_copy_change_experiment', 'isEnabled', true)) {
		return children;
	}

	return (
		<div
			ref={ref}
			css={styles.container}
			// @ts-ignore, need to dynamically set variable value for keyframes
			style={{ '--stroke-perimeter': perimeter }}
		>
			<div>
				{Object.keys(rectProps).map((key) => (
					<Fragment key={key}>
						<svg height="100%" width="100%" xmlns="http://www.w3.org/2000/svg" css={styles.svg}>
							<rect
								data-testid={key}
								strokeDasharray={calculateDashArray(key)}
								rx={borderRadius ? '4' : '0'}
								ry={borderRadius ? '4' : '0'}
								// need x and y coordinates to ensure the radius looks smooth, technically this shifts things slightly, but looks better than the alternatives
								x="1"
								y="1"
								// height and width adjustment to account for x and y coordinates
								height="94%"
								width="99%"
								stroke={rectProps[key][theme]}
								strokeWidth={token('space.025')}
							/>
						</svg>
					</Fragment>
				))}
			</div>
			<div>{children}</div>
		</div>
	);
};
